import { useState, useRef, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  title: string;
  location: string;
  date?: string;
}

export function BeforeAfterSlider({
  beforeImage,
  afterImage,
  title,
  location,
  date,
}: BeforeAfterSliderProps) {
  const [position, setPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback((clientX: number) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percent = Math.max(0, Math.min((x / rect.width) * 100, 100));
    setPosition(percent);
  }, []);

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleMouseMove = useCallback(
    (e: globalThis.MouseEvent) => {
      if (isDragging) handleMove(e.clientX);
    },
    [isDragging, handleMove],
  );

  const handleTouchMove = useCallback(
    (e: globalThis.TouchEvent) => {
      if (isDragging) handleMove(e.touches[0].clientX);
    },
    [isDragging, handleMove],
  );

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleMouseUp);
    } else {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove]);

  return (
    <div className="flex flex-col gap-4">
      <div
        ref={sliderRef}
        className="relative w-full aspect-square cursor-ew-resize select-none"
        onMouseDown={(e) => {
          setIsDragging(true);
          handleMove(e.clientX);
        }}
        onTouchStart={(e) => {
          setIsDragging(true);
          handleMove(e.touches[0].clientX);
        }}
      >
        <div className="absolute inset-0 rounded-[16px] overflow-hidden pointer-events-none">
          {/* After Image (Background) */}
          <img
            src={afterImage}
            alt={`After ${title} in ${location} - Long Vista Remodeling`}
            title={`${title.toLowerCase().replace(/\s+/g, "-")}-${location.split(",")[0].toLowerCase().trim().replace(/\s+/g, "-")}-after.jpg`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width="800"
            height="800"
          />

          {/* After Badge */}
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-black/70 text-white text-[8px] sm:text-[10px] uppercase tracking-wider font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full z-10">
            After
          </div>

          {/* Before Image (Foreground overlay) */}
          <div
            className="absolute inset-0 w-full h-full overflow-hidden z-20"
            style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
          >
            <img
              src={beforeImage}
              alt={`Before ${title} in ${location} - Long Vista Remodeling`}
              title={`${title.toLowerCase().replace(/\s+/g, "-")}-${location.split(",")[0].toLowerCase().trim().replace(/\s+/g, "-")}-before.jpg`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              width="800"
              height="800"
            />

            {/* Before Badge */}
            <div className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-black/70 text-white text-[8px] sm:text-[10px] uppercase tracking-wider font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full">
              Before
            </div>
          </div>
        </div>

        {/* Divider Line */}
        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none shadow-[0_0_10px_rgba(0,0,0,0.5)] z-30"
          style={{ left: `${position}%`, transform: "translateX(-50%)" }}
        >
          {/* Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xl text-black border border-gray-200">
            <MoveHorizontal className="w-5 h-5 text-gray-800" />
          </div>
        </div>
      </div>

      <div className="text-left px-1">
        <div className="font-bold text-gray-900 text-sm sm:text-lg mb-0.5 sm:mb-1 truncate">
          {title}
        </div>
        <div className="text-xs sm:text-sm text-gray-600 truncate">
          {location}
        </div>
        {date && (
          <div className="text-xs sm:text-sm text-gray-500 mt-0.5">{date}</div>
        )}
      </div>
    </div>
  );
}
